import type { SimulatorField } from '@vedalib/editor/lib/elements'
import type { MouseEvent } from 'react'
import { useEffect, useRef, useState } from 'react'

import { notEmpty } from 'utils/notEmpty'

import {
  clampCoordinates,
  getOppositePoint,
  getPercentageCoordinate,
  updateAreaSize,
} from '../../utils/getCoordinates'

export type ResizeDirection = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

const MIN_COORDINATE = 0
const MAX_COORDINATE = 100

const getSizeAsPercent = (value: number | string, containerSize: number): number => {
  if (typeof value === 'number') {
    return value
  }
  const numericValue = parseFloat(value) || 0
  if (value.endsWith('px')) {
    return (numericValue / containerSize) * 100
  }
  return numericValue
}

interface IFieldDragProps {
  boxNode: HTMLDivElement | HTMLImageElement | null
  list: SimulatorField[]
  updateField: (item: SimulatorField) => void
  enabled: boolean
  setActiveId: (id: string | null) => void
}

export const useFieldDrag = ({
  enabled,
  boxNode,
  setActiveId,
  list,
  updateField,
}: IFieldDragProps) => {
  const startPoint = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const [editField, setEditField_raw] = useState<SimulatorField | null>(null)
  const editFieldRef = useRef<SimulatorField | null>(null)
  const setEditField = (field: SimulatorField | null) => {
    editFieldRef.current = field
    setEditField_raw(field)
  }
  const activeListeners = useRef<Record<string, (e: Event) => void>>({})

  const stopMove = () => {
    setEditField(null)
    if (activeListeners.current.mousemove) {
      document.removeEventListener('mousemove', activeListeners.current.mousemove, true)
    }
    if (activeListeners.current.mouseup) {
      document.removeEventListener('mouseup', activeListeners.current.mouseup, true)
    }
  }

  const isResizing = useRef(false)

  const startMoveHandle = (field: SimulatorField, params?: { isResize?: boolean }) => {
    setEditField(field)
    isResizing.current = params?.isResize || false
    const onMove = onMouseMove(field, params)
    document.addEventListener('mousemove', onMove, true)
    document.addEventListener('mouseup', onMouseUp, true)
    activeListeners.current = {
      mousemove: onMove,
      mouseup: onMouseUp,
    }
  }

  const onMouseMove =
    (field: SimulatorField, params?: { isResize?: boolean }) => (event: Event) => {
      const e = event as unknown as MouseEvent
      const imgRect = boxNode?.getBoundingClientRect()
      e.stopPropagation()
      e.preventDefault()
      if (!imgRect) {
        return
      }

      const { currentX, currentY } = getPercentageCoordinate(e.clientX, e.clientY, imgRect)
      const clampedCoords = clampCoordinates(
        currentX,
        currentY,
        0,
        0,
        MIN_COORDINATE,
        MAX_COORDINATE,
      )

      if (params?.isResize) {
        const updatedArea = updateAreaSize(startPoint.current, clampedCoords)
        setEditField({ ...field, ...updatedArea })
      } else {
        const newX = field.x + (clampedCoords.x - startPoint.current.x)
        const newY = field.y + (clampedCoords.y - startPoint.current.y)

        const widthPercent = getSizeAsPercent(field.width, imgRect.width)
        const heightPercent = getSizeAsPercent(field.height, imgRect.height)

        const newCoordinates = clampCoordinates(
          newX,
          newY,
          widthPercent,
          heightPercent,
          MIN_COORDINATE,
          MAX_COORDINATE,
        )
        setEditField({ ...field, ...newCoordinates })
      }
    }

  const onMouseUp = () => {
    if (editFieldRef.current) {
      setActiveId(editFieldRef.current.id)
      updateField(editFieldRef.current)
    }
    stopMove()
  }

  const onDrag = (e: MouseEvent<HTMLDivElement>, field: SimulatorField) => {
    if (!enabled || editFieldRef.current) {
      return
    }
    e.stopPropagation()
    const imgRect = boxNode?.getBoundingClientRect()

    setActiveId(field.id)

    if (!imgRect) {
      return
    }
    const { currentX, currentY } = getPercentageCoordinate(e.clientX, e.clientY, imgRect)
    startPoint.current = { x: currentX, y: currentY }

    startMoveHandle(field)
  }

  const onResize = (
    e: MouseEvent<HTMLDivElement>,
    field: SimulatorField,
    direction: ResizeDirection,
  ) => {
    if (!enabled) {
      return
    }
    e.stopPropagation()
    setActiveId(field.id)
    const imgRect = boxNode?.getBoundingClientRect()
    if (!imgRect) {
      return
    }
    const numericField = {
      ...field,
      width: getSizeAsPercent(field.width, imgRect.width),
      height: getSizeAsPercent(field.height, imgRect.height),
    }
    startPoint.current = getOppositePoint(numericField, direction)
    startMoveHandle(field, { isResize: true })
  }

  useEffect(() => {
    return () => {
      stopMove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    onDrag,
    onResize,
    items: [...list.filter((f) => f.id !== editField?.id), editField].filter(notEmpty),
  }
}
